import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  FileSignature,
  FileText,
  Loader2,
  Lock,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { ContractDocument } from '../components/ContractDocument';
import { OutboundLink } from '../components/LeavingDialog';
import { PrintContractButton, PrintableContract } from '../components/PrintContract';

/**
 * Where a customer signs.
 *
 * The only screen in the application designed for somebody who has never seen
 * it before and will probably never see it again. It has to work on a phone, on
 * a bad connection, for a person who is deciding whether this is genuine — so:
 *
 *  - **The figures come first, above the document.** Somebody deciding whether
 *    a link is real recognises the price before they recognise anything else,
 *    and somebody who was quoted KES 2,499 needs to see 2,499 without reading
 *    fourteen clauses to find it.
 *  - **The company's signature is already there**, shown before theirs is
 *    asked for. A document with an empty company block looks unfinished, which
 *    is exactly what a fraudulent one would look like.
 *  - **No account, no password, no cookie banner obstruction.** The link is the
 *    authorisation.
 */

const kes = (amount: number): string => `KES ${amount.toLocaleString('en-KE')}`;

const onDate = (iso: string): string =>
  new Intl.DateTimeFormat('en-KE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Africa/Nairobi',
  }).format(new Date(iso));

export function SignContractPage() {
  const { token = '' } = useParams();
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [done, setDone] = useState<{ reference: string; signedAt: string } | null>(null);
  const [signedName, setSignedName] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const contract = useQuery({
    queryKey: ['sign', token],
    queryFn: () => api.contractToSign(token),
    retry: false,
  });

  const sign = useMutation({
    mutationFn: () => api.signContract(token, { typedName, agreed }),
    onSuccess: (result) => {
      // Kept because the confirmation screen puts it on the printable sheet:
      // `data` was fetched before signing and does not carry it.
      setSignedName(typedName.trim());
      setDone({ reference: result.reference, signedAt: result.signedAt });
    },
  });

  if (contract.isPending) {
    return (
      <Frame>
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-500" aria-label="Loading" />
        </div>
      </Frame>
    );
  }

  if (contract.isError) {
    return (
      <Frame>
        <div className="card text-center">
          <XCircle className="mx-auto h-10 w-10 text-danger-500" aria-hidden="true" />
          <h1 className="mt-3 font-heading text-xl">This link does not work</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {(contract.error as ApiError).message}
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Nothing has gone wrong with your agreement — links expire, and we can send you a
            fresh one. Reply to the email you received, or call us.
          </p>
        </div>
      </Frame>
    );
  }

  const data = contract.data;

  if (done) {
    return (
      <Frame>
        <div className="card text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-secondary-500" aria-hidden="true" />
          <h1 className="mt-3 font-heading text-2xl">Signed. Thank you.</h1>
          <p className="mt-2 text-muted-foreground">
            Agreement <strong>{done.reference}</strong>, signed {onDate(done.signedAt)}.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            We have emailed you a copy. Neither of us can change this document now — if
            something needs correcting we will issue a new version for you to sign, and this
            one stays readable so you can always see what you originally agreed to.
          </p>
          <div className="mt-6 rounded-xl bg-muted p-4 text-left text-sm">
            <p className="font-medium">What happens next</p>
            <p className="mt-1 text-muted-foreground">
              Our team will be in touch to book your installation. If you need us before then,
              call {data.companyPhone}.
            </p>
          </div>

          {/*
            * Reading it back, on the screen that says it worked.
            *
            * This screen used to end at "what happens next", with no way to
            * see or keep what had just been signed — and it is a dead end by
            * construction, because signing spends the link it was reached on.
            * So the document is opened in place and the printable sheet lives
            * here too, which is what makes Download or print produce the
            * agreement rather than a blank page.
            */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 border-t border-border pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setReviewing((open) => !open)}
              aria-expanded={reviewing}
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              {reviewing ? 'Hide the agreement' : 'Review the agreement'}
            </button>
            <PrintContractButton />
          </div>

          {reviewing && (
            <div className="mt-3 max-h-[26rem] overflow-y-auto rounded-xl bg-muted/50 p-4 text-left">
              <ContractDocument body={data.body} className="text-sm" />
            </div>
          )}
        </div>

        {/*
          * The sheet the print dialogue turns into a PDF. `data` was fetched
          * before signing, so the customer's own signature is not on it —
          * it is composed from what they actually typed a moment ago, which
          * is the same string the server recorded.
          */}
        <PrintableContract
          title={data.title}
          reference={done.reference}
          company={data.companyName}
          body={data.body}
          issuedOn={done.signedAt}
          standing="Executed by both parties"
          parties={[
            { role: 'The provider', name: data.companyName },
            {
              role: 'The customer',
              name: data.customerName,
              lines: [data.packageName, `${data.termMonths} months from ${onDate(data.effectiveFrom)}`],
            },
          ]}
          counterpartyLabel="The customer"
          signatures={[
            ...(data.company
              ? [
                  {
                    party: 'company' as const,
                    name: data.company.signerName,
                    title: data.company.signerTitle,
                    signedAt: data.company.signedAt,
                  },
                ]
              : []),
            {
              party: 'counterparty' as const,
              name: signedName,
              title: null,
              signedAt: done.signedAt,
            },
          ]}
        />
      </Frame>
    );
  }

  const alreadySigned = data.customerSignature !== null;

  return (
    <Frame>
      <div className="space-y-4">
        <header className="text-center">
          <h1 className="font-heading text-2xl">{data.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Agreement {data.reference} · {data.companyName}
          </p>
        </header>

        {/* The figures, before the words. This is what somebody checks first. */}
        <section className="card">
          <h2 className="mb-3 font-heading text-base">What you are agreeing to</h2>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Fact label="Package" value={data.packageName} />
            <Fact
              label="Speed"
              value={data.speedMbps ? `Up to ${data.speedMbps} Mbps` : '—'}
            />
            <Fact label="Monthly" value={kes(data.monthlyFeeKes)} strong />
            <Fact
              label="Installation"
              value={data.installFeeKes === 0 ? 'Free' : kes(data.installFeeKes)}
            />
            {data.depositKes > 0 && <Fact label="Deposit" value={kes(data.depositKes)} />}
            <Fact label="Term" value={`${data.termMonths} months`} />
            <Fact label="Starts" value={onDate(data.effectiveFrom)} />
          </dl>
        </section>

        {/* Already signed for us, and said so before we ask for theirs. */}
        {data.company && (
          <div className="flex items-start gap-3 rounded-xl border border-secondary-200 bg-secondary-50/50 p-4">
            <ShieldCheck
              className="mt-0.5 h-5 w-5 shrink-0 text-secondary-600"
              aria-hidden="true"
            />
            <div className="text-sm">
              <p className="font-medium">Already signed by us</p>
              <p className="mt-0.5 text-muted-foreground">
                {data.company.signerName}
                {data.company.signerTitle ? `, ${data.company.signerTitle}` : ''} — on{' '}
                {onDate(data.company.signedAt)}.
              </p>
            </div>
          </div>
        )}

        <section className="card">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-base">
            <FileSignature className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            The agreement in full
          </h2>
          {/*
            The frozen document, exactly as it was when it was sent.

            This was a `<pre>` of the raw source, on the argument that a renderer
            between the stored text and the signature risks somebody agreeing to
            something other than what was kept. The argument is right; the
            conclusion was not. A customer was reading `# Internet Service
            Agreement` and `**KES 1,499**` — hashes and asterisks — which does
            not look like a contract so much as something half-finished, and
            that is a poor thing to feel while signing one.

            `ContractDocument` renders faithfully: every character of text in
            the source appears in the output, nothing is reordered or hidden,
            and there is no path from the source to raw HTML. The stored text is
            still the thing signed and is still served verbatim.
          */}
          {/*
            * Downloadable before signing as well as after — somebody reading
            * twenty clauses on a phone should be able to keep the thing they
            * are being asked to agree to, not only the thing they agreed to.
            * The button prints the sheet at the bottom of this page, which
            * carries whichever signatures exist at the time.
            */}
          <div className="mb-3 flex justify-end print:hidden">
            <PrintContractButton />
          </div>
          <div className="max-h-[26rem] overflow-y-auto rounded-xl bg-muted/50 p-4">
            <ContractDocument body={data.body} className="text-sm" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Our full terms and privacy policy are at{' '}
            <OutboundLink href={data.termsUrl} className="text-primary-600 underline">
              gospeednet.co.ke
            </OutboundLink>
            .
          </p>
        </section>

        {alreadySigned ? (
          <div className="card flex items-start gap-3">
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-secondary-600" aria-hidden="true" />
            <div className="text-sm">
              <p className="font-medium">You have already signed this.</p>
              <p className="mt-0.5 text-muted-foreground">
                Signed by {data.customerSignature?.signerName} on{' '}
                {onDate(data.customerSignature!.signedAt)}, and by {data.companyName}. It is
                locked and cannot be changed — use <strong>Download or print</strong> above to
                keep a copy. This page stays here for a year; after that, ask us and we will
                send it again.
              </p>
            </div>
          </div>
        ) : data.refusal ? (
          <div className="card flex items-start gap-3">
            <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger-500" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">{data.refusal}</p>
          </div>
        ) : (
          <section className="card">
            <h2 className="font-heading text-base">Sign</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Read it before you sign. If anything is not what you were told, do not sign —
              call us on {data.companyPhone} and we will correct it and send a new one.
            </p>

            <label className="mt-4 block">
              <span className="text-sm font-medium">Type your full name</span>
              <input
                type="text"
                className="input mt-1"
                value={typedName}
                onChange={(event) => setTypedName(event.target.value)}
                placeholder={data.customerName}
                autoComplete="name"
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                As it appears on this agreement: {data.customerName}
              </span>
            </label>

            {/*
              What they have just written, shown as a signature.

              A typed name in a form field does not feel like signing anything,
              and this is the moment a person commits to paying for a year. The
              preview is the same characters in a hand — it adds no legal weight
              and claims none, and what is stored is still the typed string. It
              exists so the act reads as an act.

              Empty until they have typed something worth showing, rather than a
              hopeful empty box.
            */}
            {typedName.trim().length >= 2 && (
              <figure className="mt-4 rounded-xl border border-border bg-card px-4 pb-3 pt-5">
                <p className="signature-ink text-3xl leading-tight">{typedName.trim()}</p>
                <figcaption className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
                  Signature of {data.customerName}
                </figcaption>
              </figure>
            )}

            <label className="mt-4 flex items-start gap-2.5">
              <input
                type="checkbox"
                className="mt-1"
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
              />
              <span className="text-sm">
                I have read this agreement and I accept its terms.
              </span>
            </label>

            {sign.isError && (
              <p className="mt-3 rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
                {(sign.error as ApiError).message}
              </p>
            )}

            <button
              type="button"
              className="btn-primary mt-5 w-full justify-center"
              disabled={sign.isPending || typedName.trim().length < 3 || !agreed}
              onClick={() => sign.mutate()}
            >
              {sign.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <FileSignature className="h-4 w-4" aria-hidden="true" />
              )}
              Sign this agreement
            </button>

            <p className="mt-3 text-xs text-muted-foreground">
              Signing records your name, the date and time, and a fingerprint of this exact
              document — so neither of us can later be shown a different version.
            </p>
          </section>
        )}

        <footer className="pb-6 text-center text-xs text-muted-foreground">
          {data.companyName} · {data.companyPhone} · {data.companyEmail}
        </footer>

        {/*
          * The printable sheet. Hidden on screen and the only thing on the
          * page when printed, so what a customer saves is the document with
          * the letterhead and the signatures on it — not a screenshot of an
          * application, with a scrollbar through the middle of clause 9.
          */}
        <PrintableContract
          title={data.title}
          reference={data.reference}
          company={data.companyName}
          body={data.body}
          issuedOn={data.company?.signedAt ?? null}
          standing={
            data.customerSignature ? 'Executed by both parties' : 'Awaiting the customer’s signature'
          }
          parties={[
            { role: 'The provider', name: data.companyName },
            {
              role: 'The customer',
              name: data.customerName,
              lines: [data.packageName, `${data.termMonths} months from ${onDate(data.effectiveFrom)}`],
            },
          ]}
          counterpartyLabel="The customer"
          signatures={[
            ...(data.company
              ? [
                  {
                    party: 'company' as const,
                    name: data.company.signerName,
                    title: data.company.signerTitle,
                    signedAt: data.company.signedAt,
                  },
                ]
              : []),
            ...(data.customerSignature
              ? [
                  {
                    party: 'counterparty' as const,
                    name: data.customerSignature.signerName,
                    title: data.customerSignature.signerTitle,
                    signedAt: data.customerSignature.signedAt,
                  },
                ]
              : []),
          ]}
        />
      </div>
    </Frame>
  );
}

function Fact({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 ${strong ? 'text-lg font-semibold' : 'text-sm font-medium'}`}>
        {value}
      </dd>
    </div>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/40 px-4 py-8">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex justify-center">
          <img src="/brand/logo.png" alt="GoSpeedNet" className="h-9 w-auto" />
        </div>
        {children}
      </div>
    </div>
  );
}
